import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Save, ArrowLeft, History, Send, Loader2 } from "lucide-react";
import { TEMPLATES } from "@/constants/testIds";
import { useTemplateEditor } from "@/hooks/useTemplateEditor";
import { TemplateForm } from "@/components/templates/TemplateForm";
import { VariablePanel } from "@/components/templates/VariablePanel";
import { TemplatePreviewPane } from "@/components/templates/TemplatePreviewPane";
import { VersionsSheet } from "@/components/templates/VersionsSheet";
import { TestMailDialog } from "@/components/templates/TestMailDialog";
import { ImagePickerDialog } from "@/components/templates/ImagePickerDialog";
import { ImageStrategyCard } from "@/components/templates/ImageStrategyCard";
import { VersionCompareDialog } from "@/components/templates/VersionCompareDialog";
import { useCidAnalysis } from "@/hooks/useCidAnalysis";

export default function TemplateEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const {
    template, setTemplate,
    variables, users, images,
    previewUserId, setPreviewUserId, previewHtml,
    changeNote, setChangeNote,
    versions, saving, loaded,
    doSave, loadVersions, restoreVersion, sendTestMail,
    insertVariable, insertImage,
  } = useTemplateEditor(id, isNew);
  const { analysis, analyzing, analyze } = useCidAnalysis(template.html_content, previewUserId);

  // Sheet/dialog open flags are UI-only, so they stay in the page.
  const [openVersions, setOpenVersions] = useState(false);
  const [openTestMail, setOpenTestMail] = useState(false);
  const [openImagePicker, setOpenImagePicker] = useState(false);
  const [compareBase, setCompareBase] = useState(null);

  const handleLoadVersions = async () => {
    await loadVersions();
    setOpenVersions(true);
  };

  const handleRestore = async (v) => {
    const ok = await restoreVersion(v);
    if (ok) {
      setOpenVersions(false);
      setCompareBase(null);
    }
  };

  const handleCompare = (v) => {
    setOpenVersions(false);
    setCompareBase(v);
  };

  if (!loaded) {
    return (
      <div className="flex items-center gap-2 py-24 justify-center text-sm text-slate-500"
        data-testid="template-editor-loading">
        <Loader2 className="w-4 h-4 animate-spin text-orange-600" strokeWidth={1.75} /> Cargando plantilla...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={TEMPLATES.editorRoot}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/templates")}
            className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          </Button>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
              {isNew ? "Nueva" : "Editando"}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {template.name || "Plantilla sin título"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <>
              <Button variant="outline" onClick={handleLoadVersions}
                className="border-slate-200" data-testid={TEMPLATES.versionsBtn}>
                <History className="w-4 h-4 mr-2" strokeWidth={1.75} /> Versiones
              </Button>
              <Button variant="outline" onClick={() => setOpenTestMail(true)}
                className="border-slate-200" data-testid={TEMPLATES.testMailBtn}>
                <Send className="w-4 h-4 mr-2" strokeWidth={1.75} /> Enviar prueba
              </Button>
            </>
          )}
          <Button onClick={doSave} disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
            data-testid={TEMPLATES.saveBtn}>
            <Save className="w-4 h-4 mr-2" strokeWidth={1.75} />
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <TemplateForm
          template={template}
          onChange={setTemplate}
          isNew={isNew}
          changeNote={changeNote}
          onChangeNote={setChangeNote}
        />
        <div className="lg:col-span-2 space-y-4">
          <ImageStrategyCard
            value={template.image_strategy}
            onChange={(v) => setTemplate((prev) => ({ ...prev, image_strategy: v }))}
            analysis={analysis}
            analyzing={analyzing}
            onAnalyze={analyze}
          />
          <VariablePanel
            variables={variables}
            images={images}
            onInsertVariable={insertVariable}
            onOpenImagePicker={() => setOpenImagePicker(true)}
            onQuickInsertImage={(img) => insertImage(img)}
          />
          <TemplatePreviewPane
            users={users}
            previewUserId={previewUserId}
            onUserChange={setPreviewUserId}
            previewHtml={previewHtml}
            htmlContent={template.html_content}
            imageStrategy={template.image_strategy}
          />
        </div>
      </div>

      <VersionsSheet
        open={openVersions}
        onOpenChange={setOpenVersions}
        versions={versions}
        onRestore={handleRestore}
        onCompare={handleCompare}
      />
      <VersionCompareDialog
        baseVersion={compareBase}
        onOpenChange={(o) => { if (!o) setCompareBase(null); }}
        versions={versions}
        previewUserId={previewUserId}
        onRestore={handleRestore}
      />
      <TestMailDialog
        open={openTestMail}
        onOpenChange={setOpenTestMail}
        users={users}
        previewUserId={previewUserId}
        onPreviewUserChange={setPreviewUserId}
        onSend={sendTestMail}
      />
      <ImagePickerDialog
        open={openImagePicker}
        onOpenChange={setOpenImagePicker}
        images={images}
        onInsert={insertImage}
      />
    </div>
  );
}
